import { Fragment, useMemo, useState } from 'react'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { formatDateTime } from '../utils/datetime'
import toast from 'react-hot-toast'
import { FaBox, FaBoxOpen, FaSearch, FaEye, FaArrowLeft, FaChevronDown, FaChevronRight, FaMapMarkerAlt, FaTruck } from 'react-icons/fa'
import { bookingService } from '../services'
import { Card, PageHeader, StatusBadge, LoadingOverlay, EmptyState, Select, Button, PhoneTailVerifyModal, MobileCard } from '../components/common'
import { useAuth } from '../hooks/useAuth'

/**
 * 【报名详情并入本页 · 2026-07-31 —— 原独立路由 /cargo/:bookingCode 与 CargoDetailPage 已删除】
 *
 * 原先「详情」是跳到独立页面，那个页面挂载后再调 /detail-by-code 把同一条记录取第二遍。
 * 而列表接口返回的就是完整实体（39 个字段，与详情接口逐字段一致），进详情前数据早已在手，
 * 那次请求纯属重复取数。
 *
 * 之所以会重复，是因为路由页面只能看见 URL：点击不携带数据，页面只能拿单号重新取。
 * 这是路由页面换取「可刷新、可分享」的固有代价，而这里两样都不需要 ——
 * 报名详情含联系人与手机号，一条谁都能打开的链接本身就是问题（单号是顺序号、可枚举）。
 * 日后真要做分享，应当像推荐链接那样在地址里带随机令牌，而不是把单号当凭证。
 *
 * 现在详情是本页的一个视图：点详情切过去，点「返回我的报名」切回来，数据直接用当前行的，
 * 零请求、不离开页面，搜索词与查询结果自然还在（原先为此打的 sessionStorage 恢复补丁已删除）。
 * 版式与原详情页保持一致，不是弹窗。
 *
 * ⚠️ `/blade-portal/booking/detail-by-code` 至此前端零调用点。该接口**免鉴权、不校验手机后四位**，
 * 谁拿到单号都能取到完整记录 —— 不要再把它接回前端；需要按单号取数请走 verify-by-code。
 *
 * 连带变更：客户中心「最近报名」原本也链到 /cargo/<单号>，改为 /my-consolidation?code=<单号>，
 * 由本页按单号定位到列表中那一行（同样零额外请求）。
 */
const statusOptions = [
  { value: '', label: '全部状态' },
  { value: 'pending', label: '报价中' },
  { value: 'confirmed', label: '已报价' },
  { value: 'rejected', label: '已拒绝' },
  { value: 'completed', label: '已完成' },
  { value: 'cancelled', label: '已取消' },
]

export default function CargoPage() {
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuth()
  // ?code=<单号>：客户中心「最近报名」跳过来时用它定位到某一行（那边只有单号，没有整条数据）
  const [searchParams] = useSearchParams()
  const [filters, setFilters] = useState(() => ({ status: '', search: searchParams.get('code') || '' }))
  const [actionLoading, setActionLoading] = useState({})
  const [rejectModal, setRejectModal] = useState({ visible: false, booking: null })
  const [acceptModal, setAcceptModal] = useState({ visible: false, booking: null })
  const [expandedRows, setExpandedRows] = useState({})
  // 凭「报名单号 + 手机后四位」校验查得的单据（游客、或登录用户查询非本人订单时使用）
  const [queryResult, setQueryResult] = useState(null)
  // 校验通过的那四位。游客单的拒绝/接受走 by-code 接口，需要再次出示这个凭证 ——
  // 后端没有游客会话，认不出「刚才校验过」。留在内存里，用户不用再输一遍。
  const [verifiedPhoneTail, setVerifiedPhoneTail] = useState('')
  const [verifyModal, setVerifyModal] = useState({ visible: false, code: '' })
  const [verifyLoading, setVerifyLoading] = useState(false)
  // 非空时本页切换到详情视图；数据就是列表/查询结果里那一行，不再另取
  const [detailBooking, setDetailBooking] = useState(null)
  // 详情是本页的一个视图（不是独立路由），所以多挂一层单号而不是另起面包屑
  const breadcrumb = useBreadcrumb(
    detailBooking ? [{ label: detailBooking.booking_code || '详情' }] : []
  )

  // 放弃本次查询：搜索条件与查询结果一起清干净
  const resetSearch = () => {
    setFilters({ status: '', search: '' })
    setQueryResult(null)
  }

  // 仅登录用户拉取本人报名列表；游客不请求，避免 401 触发跳转登录
  const { data: bookings, isLoading } = useQuery({
    queryKey: ['bookings', 'my-list'],
    queryFn: () => bookingService.getBookings({ size: 100 }),
    enabled: isAuthenticated,
  })

  // 包 useMemo：不包的话每次渲染都是新数组，下面那个 useMemo 的依赖恒变、过滤白算一遍
  const bookingRecords = useMemo(
    () => (Array.isArray(bookings) ? bookings : (bookings?.records || [])),
    [bookings]
  )

  const filteredBookings = useMemo(() => {
    return bookingRecords.filter((booking) => {
      const normalizedStatus = booking.status === 'in_transit' ? 'confirmed' : booking.status
      if (filters.status && normalizedStatus !== filters.status) return false
      if (filters.search) {
        const search = filters.search.toLowerCase()
        return (
          String(booking.booking_code || '').toLowerCase().includes(search) ||
          String(booking.cargo_name || '').toLowerCase().includes(search)
        )
      }
      return true
    })
  }, [bookingRecords, filters])

  const toVolume = (booking) => {
    if (booking.length && booking.width && booking.height) {
      const volume = (parseFloat(booking.length) * parseFloat(booking.width) * parseFloat(booking.height)) / 1000000
      if (!Number.isNaN(volume)) {
        return `${volume.toFixed(4)} CBM`
      }
    }
    return '-'
  }

  const toVolumeRatio = (booking) => {
    if (booking.volume === null || booking.volume === undefined || booking.volume === '') return '-'
    const num = parseFloat(booking.volume)
    return Number.isNaN(num) ? '-' : num.toFixed(4)
  }

  const renderQuotedAmount = (booking) => {
    const status = String(booking.status || '').toLowerCase()
    const amount = parseFloat(booking.quoted_amount)
    if (status === 'pending' || Number.isNaN(amount) || amount <= 0) {
      return <span className="text-gray-400">-</span>
    }
    return <span className="font-medium text-primary-600">¥{amount.toFixed(2)}</span>
  }

  const withLoading = async (id, task) => {
    setActionLoading((prev) => ({ ...prev, [id]: true }))
    try {
      await task()
      await queryClient.invalidateQueries({ queryKey: ['bookings'] })
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: false }))
    }
  }

  const handleReject = (booking, byCode = false) => {
    if (booking.status === 'completed') {
      toast.error('若要拒绝请联系客服处理')
      return
    }
    if (booking.status === 'cancelled') {
      toast.error('报名单已取消，无需拒绝')
      return
    }
    setRejectModal({ visible: true, booking, byCode })
  }

  /**
   * 走 id 还是走 by-code：
   *   本人列表里的单 -> 按 id（后端按 customerId 过滤，天然只能动自己的）
   *   校验查得的游客单 -> 按「单号 + 后四位」（这类单没有 customer_id，按 id 的接口匹配不到）
   * 校验查得的「有主单」不会走到这里 —— 按钮是禁用的。
   */
  // 无主单（游客提交）。不能只判空：库里这一列带默认值 -1，游客单落库是 -1 而不是 NULL
  // （建表脚本注释写的是「NULL 表示游客」，与实际不符，历史数据两种都可能有）。
  // customer_id 经 ToStringSerializer 序列化成字符串，真实客户 id 是雪花号、恒为正数。
  const isGuestBooking = (booking) => {
    const owner = Number(booking?.customer_id)
    return !booking?.customer_id || Number.isNaN(owner) || owner <= 0
  }

  const runBookingAction = async (booking, { byCode, idAction, codeAction, successText }) => {
    await withLoading(booking.id, async () => {
      if (byCode) {
        await codeAction(booking.booking_code, verifiedPhoneTail)
      } else {
        await idAction(booking.id)
      }
      toast.success(successText)
    })
    // 校验查得的那条不在本人列表里，invalidate 刷不到它，得按同一凭证重新取一遍状态
    if (byCode) {
      try {
        setQueryResult(await bookingService.verifyBookingByCode(booking.booking_code, verifiedPhoneTail))
      } catch {
        /* 状态已经改成功了，这里只是回显失败，不再打扰用户 */
      }
    }
  }

  const confirmReject = async () => {
    const booking = rejectModal.booking
    if (!booking) return
    await runBookingAction(booking, {
      byCode: rejectModal.byCode,
      idAction: bookingService.rejectBooking,
      codeAction: bookingService.rejectBookingByCode,
      successText: '已拒绝',
    })
    setRejectModal({ visible: false, booking: null })
  }

  const handleAccept = (booking, byCode = false) => {
    if (String(booking.status || '').toLowerCase() !== 'confirmed') {
      return
    }
    setAcceptModal({ visible: true, booking, byCode })
  }

  const confirmAccept = async () => {
    const booking = acceptModal.booking
    if (!booking) return
    await runBookingAction(booking, {
      byCode: acceptModal.byCode,
      idAction: bookingService.acceptBooking,
      codeAction: bookingService.acceptBookingByCode,
      successText: '已接受',
    })
    setAcceptModal({ visible: false, booking: null })
  }

  const toggleExpandRow = (booking) => {
    const rowKey = booking.id || booking.booking_code
    setExpandedRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }))
  }

  // 点击「查询」：本人订单直接由列表过滤展示；游客或查询非本人订单则弹窗校验手机后四位
  const handleQuery = () => {
    const code = filters.search.trim()
    if (!code) {
      toast.error('请输入报名单号')
      return
    }
    setQueryResult(null)
    if (isAuthenticated) {
      const isOwn = bookingRecords.some(
        (b) => String(b.booking_code || '').toLowerCase() === code.toLowerCase()
      )
      if (isOwn) {
        // 本人订单：下方列表已按搜索词过滤展示，无需校验
        return
      }
    }
    setVerifyModal({ visible: true, code })
  }

  // 弹窗提交：校验「报名单号 + 手机后四位」，通过后展示该单
  const handleVerify = async (phoneTail) => {
    setVerifyLoading(true)
    try {
      const result = await bookingService.verifyBookingByCode(verifyModal.code, phoneTail)
      setQueryResult(result)
      setVerifiedPhoneTail(phoneTail)
      setVerifyModal({ visible: false, code: '' })
      toast.success('校验成功')
    } catch (error) {
      toast.error(error.message || '校验失败')
    } finally {
      setVerifyLoading(false)
    }
  }

  /**
   * 报名列表表格。本人列表与「校验查询结果」共用这一份渲染，两处 UI 与展示字段
   * 因此完全一致（含展开行），差别只在数据。
   *
   * byCode=true 表示这批是「校验查得」的单（不是从本人列表来的），此时能不能操作
   * 取决于这张单有没有主：
   *   - 游客提交的单（customer_id 为空）：过了后四位校验就是它唯一的凭证，可以操作，
   *     走 by-code 接口；
   *   - 账号提交的单：必须由该账号登录后操作，按钮置灰并说明原因 —— 手机后四位是
   *     弱凭证，不能拿它去动一个有主的单（后端同样会拒绝，这里不是唯一防线）。
   */
  const renderBookingTable = (records, { byCode = false } = {}) => (
    <>
      {/*
        小屏卡片视图（表格死宽 1100px，320px 屏要横拖 3 屏）。
        操作按钮与桌面表格保持完全一致（含禁用态），只是竖排进卡片。
      */}
      <div className="md:hidden space-y-3 p-4">
        {records.map((booking) => {
          const canAct = !byCode || isGuestBooking(booking)
          const key = booking.id || booking.booking_code
          const lockedHint = '该报名由账号提交，请登录该账号后操作'
          const quoted = parseFloat(booking.quoted_amount)
          const hasQuote = String(booking.status || '').toLowerCase() !== 'pending'
            && !Number.isNaN(quoted) && quoted > 0
          const expanded = expandedRows[key]

          return (
            <MobileCard
              key={key}
              title={<span className="font-mono">{booking.booking_code || '-'}</span>}
              subtitle={[booking.cargo_name, booking.destination_country].filter(Boolean).join('  ·  ')}
              badge={<StatusBadge status={booking.status} />}
              fields={[
                hasQuote && { label: '报价金额', value: renderQuotedAmount(booking) },
                ...(expanded
                  ? [
                    { label: '物品类型', value: booking.item_type },
                    { label: '重量', value: booking.weight ? `${booking.weight} KG` : null },
                    { label: '体积', value: toVolume(booking) },
                    { label: '体积比', value: toVolumeRatio(booking) },
                    { label: '运货方式', value: booking.logistics_option || booking.shipping_method },
                  ]
                  : []),
              ]}
              actions={
                <>
                  <button
                    type="button"
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-primary-200 text-primary-600 hover:bg-primary-50"
                    onClick={() => setDetailBooking(booking)}
                  >
                    <FaEye className="w-3.5 h-3.5" />
                    详情
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!canAct || !!actionLoading[booking.id] || booking.status === 'rejected'}
                    title={canAct ? undefined : lockedHint}
                    onClick={() => handleReject(booking, byCode)}
                  >
                    拒绝
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-green-200 text-green-600 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!canAct || !!actionLoading[booking.id] || String(booking.status || '').toLowerCase() !== 'confirmed'}
                    title={canAct ? undefined : lockedHint}
                    onClick={() => handleAccept(booking, byCode)}
                  >
                    接受
                  </button>
                  <button
                    type="button"
                    className="ml-auto px-2 py-1.5 text-xs text-gray-500 hover:text-primary-600"
                    onClick={() => toggleExpandRow(booking)}
                  >
                    {expanded ? '收起' : '更多'}
                  </button>
                </>
              }
            />
          )
        })}
      </div>

      <div className="hidden md:block overflow-x-auto">
      <table className="table min-w-[1100px]">
        <thead>
          <tr>
            <th className="whitespace-nowrap text-center" style={{ width: 50 }}></th>
            <th className="whitespace-nowrap !text-center">报名单号</th>
            <th className="whitespace-nowrap !text-center">物品名称</th>
            <th className="whitespace-nowrap !text-center">收货国家</th>
            <th className="whitespace-nowrap !text-center">报价金额</th>
            <th className="whitespace-nowrap !text-center">状态</th>
            <th className="whitespace-nowrap !text-center">操作</th>
          </tr>
        </thead>
        <tbody>
          {records.map((booking) => {
          // 本人列表里的单一律可操作；校验查得的单只有「无主」（游客提交）才可操作
          const canAct = !byCode || isGuestBooking(booking)
          const lockedHint = '该报名由账号提交，请登录该账号后操作'
          return (
            <Fragment key={booking.id || booking.booking_code}>
              <tr>
                <td className="text-center" style={{ width: 50 }}>
                  <button
                    type="button"
                    className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-full transition-colors"
                    onClick={() => toggleExpandRow(booking)}
                    title={expandedRows[booking.id || booking.booking_code] ? '收起' : '展开'}
                  >
                    {expandedRows[booking.id || booking.booking_code] ? <FaChevronDown className="w-3.5 h-3.5" /> : <FaChevronRight className="w-3.5 h-3.5" />}
                  </button>
                </td>
                <td className="font-medium whitespace-nowrap text-center">{booking.booking_code || '-'}</td>
                <td className="whitespace-nowrap text-center">{booking.cargo_name || '-'}</td>
                <td className="whitespace-nowrap text-center">{booking.destination_country || '-'}</td>
                <td className="whitespace-nowrap text-center">{renderQuotedAmount(booking)}</td>
                <td className="whitespace-nowrap text-center"><StatusBadge status={booking.status} /></td>
                <td className="whitespace-nowrap text-center">
                  <div className="flex items-center justify-center gap-3">
                    <button
                      type="button"
                      className="px-3 py-1 text-xs font-medium rounded-md border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!canAct || !!actionLoading[booking.id] || booking.status === 'rejected'}
                      title={canAct ? undefined : lockedHint}
                      onClick={() => handleReject(booking, byCode)}
                    >
                      拒绝
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1 text-xs font-medium rounded-md border border-green-200 text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!canAct || !!actionLoading[booking.id] || String(booking.status || '').toLowerCase() !== 'confirmed'}
                      title={canAct ? undefined : lockedHint}
                      onClick={() => handleAccept(booking, byCode)}
                    >
                      接受
                    </button>
                    <button
                      type="button"
                      className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-full transition-colors"
                      title="详情"
                      onClick={() => setDetailBooking(booking)}
                    >
                      <FaEye className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
              {expandedRows[booking.id || booking.booking_code] && (
                <tr>
                  <td colSpan={7} className="bg-gray-50 px-4 py-3">
                    <div className="grid grid-cols-fluid-sm gap-3 text-sm">
                      <div><span className="text-gray-500">物品类型：</span>{booking.item_type || '-'}</div>
                      <div><span className="text-gray-500">重量：</span>{booking.weight ? `${booking.weight} KG` : '-'}</div>
                      <div><span className="text-gray-500">体积：</span>{toVolume(booking)}</div>
                      <div><span className="text-gray-500">体积比：</span>{toVolumeRatio(booking)}</div>
                      <div className="md:col-span-2 lg:col-span-2"><span className="text-gray-500">运货方式：</span>{booking.logistics_option || booking.shipping_method || '-'}</div>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          )
          })}
        </tbody>
      </table>
      </div>
    </>
  )

  /**
   * 报名详情视图。版式沿用原独立详情页（PageHeader + 返回 + 基础信息/路线信息/运输与费用
   * 三块卡片），只是不再是一条路由 —— 数据由调用方把整行传进来，不发请求。
   * 「返回我的报名」只是把 detailBooking 置空，本页从未卸载，搜索词与查询结果原样还在。
   */
  const renderDetailView = (booking) => (
    <div>
      <PageHeader
        icon={FaBoxOpen}
        title="报名详情"
        subtitle={`报名单号：${booking.booking_code || '-'}`}
        breadcrumb={breadcrumb}
      />

      <div className="container py-8 space-y-6">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setDetailBooking(null)}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <FaArrowLeft /> 返回我的报名
          </button>
        </div>

        <Card>
          <Card.Header>
            <h4 className="font-semibold">基础信息</h4>
          </Card.Header>
          <Card.Body>
            <div className="grid grid-cols-fluid-sm gap-4 text-sm">
              <div>
                <p className="text-gray-500">报名单号</p>
                <p className="font-medium">{booking.booking_code || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500">物品名称</p>
                <p className="font-medium">{booking.cargo_name || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500">当前状态</p>
                <StatusBadge status={booking.status} />
              </div>
              <div>
                <p className="text-gray-500">重量</p>
                <p className="font-medium">{booking.weight || 0} KG</p>
              </div>
              <div>
                <p className="text-gray-500">体积比</p>
                <p className="font-medium">{booking.volume || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500">件数</p>
                <p className="font-medium">{booking.quantity || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500">创建时间</p>
                <p className="font-medium">{formatDateTime(booking.create_time, 'YYYY-MM-DD HH:mm')}</p>
              </div>
              <div>
                <p className="text-gray-500">联系人</p>
                <p className="font-medium">{booking.contact_name || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500">联系电话</p>
                <p className="font-medium">{booking.contact_phone || '-'}</p>
              </div>
            </div>
          </Card.Body>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <Card.Header>
              <h5 className="font-semibold flex items-center gap-2"><FaMapMarkerAlt className="text-primary-500" /> 路线信息</h5>
            </Card.Header>
            <Card.Body>
              <div className="space-y-3 text-sm">
                <div>
                  {/* 起运国家固定为「中国」：集运报名发货国家恒为中国，此处直接展示固定值 */}
                  <p className="text-gray-500">起运国家</p>
                  <p className="font-medium">中国</p>
                </div>
                <div>
                  <p className="text-gray-500">起运地址</p>
                  <p className="font-medium">{booking.pickup_address || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">目的地国家/城市</p>
                  <p className="font-medium">{booking.destination_country || '-'} {booking.destination_city || ''}</p>
                </div>
                <div>
                  <p className="text-gray-500">派送地址</p>
                  <p className="font-medium">{booking.delivery_address || '-'}</p>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <h5 className="font-semibold flex items-center gap-2"><FaTruck className="text-primary-500" /> 运输与费用</h5>
            </Card.Header>
            <Card.Body>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500">运货方式</p>
                  <p className="font-medium">{booking.shipping_method || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">服务商</p>
                  <p className="font-medium">{booking.service_provider || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">报价金额</p>
                  <p className="font-semibold text-primary-600">
                    {renderQuotedAmount(booking)}
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  )

  if (detailBooking) {
    return renderDetailView(detailBooking)
  }

  return (
    <div>
      <PageHeader
        icon={FaBox}
        title="我的报名"
        subtitle="查看和管理您的所有报名"
        breadcrumb={breadcrumb}
      />

      <div className="container py-8">
        <Card className="mb-6">
          <Card.Body>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={isAuthenticated ? '搜索单号或物品名称...' : '请输入报名单号查询'}
                    className="form-control pl-10"
                    value={filters.search}
                    onChange={(e) => {
                      setFilters({ ...filters, search: e.target.value })
                      // 校验查得的结果属于上一次查的那个单号，搜索词一改就不再对应；
                      // 且它会顶掉下方本人列表，不清掉就没法改回搜索词看自己的报名。
                      if (queryResult) setQueryResult(null)
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleQuery()}
                  />
                </div>
              </div>
              {isAuthenticated && (
                <Select
                  options={statusOptions}
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full md:w-48"
                />
              )}
              <Button onClick={handleQuery} icon={<FaSearch />} className="whitespace-nowrap">
                查询
              </Button>
            </div>
            {!isAuthenticated && (
              <p className="text-sm text-gray-500 mt-3">
                未登录状态下可凭「报名单号 + 报名手机号后四位」查询单条报名进度。
              </p>
            )}
          </Card.Body>
        </Card>

        {/* 校验查得的单据（游客 / 登录用户查询非本人订单）。
            与本人列表用同一张表格渲染；此时不再渲染下方列表卡片 ——
            否则搜索词把本人列表过滤成空，会在结果下面同时冒出「暂无报名记录」。 */}
        {queryResult ? (
          <Card>
            <Card.Header>
              <h4 className="font-semibold">查询结果</h4>
            </Card.Header>
            <Card.Body className="p-0">
              {renderBookingTable([queryResult], { byCode: true })}
            </Card.Body>
          </Card>
        ) : (
        /* 下方报名列表：仅登录用户展示本人报名 */
        isAuthenticated && (
        <Card>
          <Card.Body className="p-0">
            {isLoading ? (
              <LoadingOverlay />
            ) : filteredBookings?.length > 0 ? (
              renderBookingTable(filteredBookings)
            ) : bookingRecords.length > 0 ? (
              /* 名下有报名、只是被搜索条件筛没了。这里绝不能说「您还没有提交任何集运报名」——
                 输入他人单号时列表会即时筛空，那句话与事实相反。 */
              <EmptyState
                title="没有匹配的报名"
                description="当前账号下没有符合条件的报名。若查的是他人、或注册前以游客身份提交的报名单，请点「查询」并校验手机号后四位。"
                actionLabel="清空筛选"
                action={resetSearch}
              />
            ) : (
              <EmptyState
                title="暂无报名记录"
                description="您还没有提交任何集运报名"
                actionLabel="立即报名"
                action={() => window.location.href = '/consolidation'}
              />
            )}
          </Card.Body>
        </Card>
        ))}
      </div>

      <PhoneTailVerifyModal
        visible={verifyModal.visible}
        code={verifyModal.code}
        loading={verifyLoading}
        onCancel={() => setVerifyModal({ visible: false, code: '' })}
        onConfirm={handleVerify}
      />


      {/* 拒绝确认弹窗 */}
      {rejectModal.visible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h4 className="font-semibold">确认拒绝</h4>
              <button onClick={() => setRejectModal({ visible: false, booking: null })} className="text-gray-400 hover:text-gray-600">
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p>确认拒绝报名单 <span className="font-semibold text-red-600">{rejectModal.booking?.booking_code || ''}</span> 吗？</p>
              <div className="flex justify-end gap-3">
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => setRejectModal({ visible: false, booking: null })}
                >取消</button>
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={!!actionLoading[rejectModal.booking?.id]}
                  onClick={confirmReject}
                >确认</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 接受确认弹窗 */}
      {acceptModal.visible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h4 className="font-semibold">确认接受</h4>
              <button onClick={() => setAcceptModal({ visible: false, booking: null })} className="text-gray-400 hover:text-gray-600">
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p>确认接受报名单 <span className="font-semibold text-green-600">{acceptModal.booking?.booking_code || ''}</span> 吗？</p>
              <div className="flex justify-end gap-3">
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => setAcceptModal({ visible: false, booking: null })}
                >取消</button>
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={!!actionLoading[acceptModal.booking?.id]}
                  onClick={confirmAccept}
                >确认</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
